import { Box, CheckBox, FormField, Grid, ResponsiveContext, Select, Text, TextArea, TextInput } from "grommet";
import * as React from "react";
import AutoSizeTextArea from "../components/AutoSizeTextArea";
import Flag from "../components/Flag";
import { ImagePicker } from "../components/ImagePicker";
import ThemeBuilder from "../components/ThemeBuilder";
import TeamLogoPicker from "./TeamLogoPicker";
import TeamSplashPicker from "./TeamSplashPicker";
import { CreateTeamRequest, TeamViewModel, UpdateTeamRequest } from "./TeamsStore";

interface TeamDetailsProps {
    team: TeamViewModel | CreateTeamRequest | UpdateTeamRequest;
    isBusy: boolean;
    onChange: Function;
    size?: string;
}

const TeamDetails = ({ team, isBusy, onChange, size = "medium" }: TeamDetailsProps) => {
    const [name, setName] = React.useState(team.name || "");
    const [logoUri, setLogoUri] = React.useState(team.logoUri || "");
    const [splashUri, setSplashUri] = React.useState(team.splashUri || "");
    const [about, setAbout] = React.useState(team.about || "");
    const [countryCode, setCountryCode] = React.useState(team.countryCode || "ZA");
    const [color, setColor] = React.useState(team.color || "");
    const [isPublic, setIsPublic] = React.useState(team.isPublic || false);

    const [archived, setArchived] = React.useState(team.archived || false);

    const [logoContent, setLogoContent] = React.useState("");
    const [splashContent, setSplashContent] = React.useState("");

    React.useEffect(() => {
        onChange({ countryCode, name, about, logoUri, logoContent, splashUri, splashContent, color, isPublic, archived });
    }, [countryCode, name, about, logoUri, logoContent, splashUri, splashContent, color, isPublic, archived]);

    React.useEffect(() => {
        if (team.logoUri && logoUri != team.logoUri) {
            setLogoUri(team.logoUri);
        }
        if (team.splashUri && splashUri != team.splashUri) {
            setSplashUri(team.splashUri);
        }
    }, [team.logoUri, team.splashUri]);

    const handleLogoChange = ({ uri, content }) => {
        if (content) {
            setLogoContent(content);
            setLogoUri("");
        } else if (uri) {
            setLogoUri(uri);
            setLogoContent("");
        }
    };

    const handleSplashChange = ({ uri, content }) => {
        if (content) {
            setSplashContent(content);
            setSplashUri("");
        } else if (uri) {
            setSplashUri(uri);
            setSplashContent("");
        }
    };

    return (
        <div>
            <>
                <Grid columns={["auto"]} gap="small">
                    <Select
                        options={["ZA", "AE", "SA", "OM", "NA", "BW", "ZW"]}
                        value={countryCode}
                        size="small"
                        valueLabel={
                            <Box pad="0.5rem" direction="row" align="center">
                                <Flag country={countryCode} size="40"></Flag>
                                <Text>{countryCode}</Text>
                            </Box>
                        }
                        children={(option, index, options, { active, disabled, selected }) => (
                            <Box pad="1rem" direction="row" align="center">
                                <Flag country={option} size="40"></Flag>
                                <Text>{option}</Text>
                            </Box>
                        )}
                        onChange={({ option }) => setCountryCode(option)}
                        disabled={isBusy}
                    />

                    <CheckBox label="Public" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} disabled={isBusy} />

                    <FormField label="Team Name">
                        <TextInput value={name} onChange={(e) => setName(e.target.value)} disabled={isBusy} />
                    </FormField>

                    <FormField label="Archived">
                        <CheckBox checked={archived} onChange={(e) => setArchived(e.target.checked)} disabled={isBusy} />
                    </FormField>

                    <FormField label="About">
                        <AutoSizeTextArea value={about} onChange={(e) => setAbout(e.target.value)} disabled={isBusy} />
                    </FormField>

                    <Box fill={true}>
                        <TeamLogoPicker uri={logoUri} disabled={isBusy} onSelect={handleLogoChange}></TeamLogoPicker>
                    </Box>
                    <Box fill={true}>
                        <TeamSplashPicker uri={splashUri} disabled={isBusy} onSelect={handleSplashChange}></TeamSplashPicker>
                    </Box>
                </Grid>

                <ThemeBuilder uri={logoUri || logoContent} color={color} onSelect={(color) => setColor(color)} disabled={isBusy}></ThemeBuilder>
            </>
        </div>
    );
};

export default TeamDetails;
